const { viewportSize } = window.BAO.Utils

class ReadMoreDescription extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['trigger', 'content', 'inner']
  }

  connectedCallback () {
    super.connectedCallback()

    this.startsCollapsed = this.isInsideCollapsedPanel()

    this.updateOverflowing()
  }

  /**
   * @returns {boolean}
   */
  isInsideCollapsedPanel () {
    const panel = this.closest('[data-accordion-group-el="content"]')
    return panel?.getAttribute('aria-hidden') === 'true'
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.resizeObserver.disconnect()
  }

  setupListeners () {
    super.setupListeners()

    this.updateOverflowing = this.updateOverflowing.bind(this)

    this.listeners.add(
      this.els.trigger.element,
      'click',
      this.handleClick.bind(this),
    )

    this.resizeObserver = new window.ResizeObserver(this.updateOverflowing)
    this.resizeObserver.observe(this.els.inner.element)
  }

  /**
   * @returns {void}
   */
  handleClick () {
    const willExpand = !this.isExpanded

    this.els.content.element.setAttribute('aria-expanded', willExpand)
    this.els.trigger.element.textContent = willExpand
      ? this.dataset.readMoreTranslationReadLess
      : this.dataset.readMoreTranslationReadMore
  }

  /**
   * @returns {void}
   */
  updateOverflowing () {
    if (this.isDisabled) {
      this.classList.add('red-Description-fits')
      return
    }

    if (this.isHidden) return

    const cutoff = this.cutoff
    if (!Number.isFinite(cutoff)) return

    const fullHeight = this.els.inner.element.scrollHeight
    const isOverflowing = fullHeight > cutoff + 1

    if (isOverflowing) {
      this.style.setProperty('--ReadMore_Cutoff', `${cutoff}px`)
      this.style.setProperty('--ReadMore_Start', `${cutoff / fullHeight}fr`)
    }

    this.classList.toggle('red-Description-fits', !isOverflowing)
  }

  /**
   * @returns {number}
   */
  get cutoff () {
    const container = this.els.inner.element
    const containerRect = container.getBoundingClientRect()
    const targetLineCount = this.lines

    const treeWalker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: node =>
          node.textContent.trim().length > 0
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP,
      },
    )

    const rawRects = []
    let node
    const range = document.createRange()

    while ((node = treeWalker.nextNode())) {
      range.selectNodeContents(node)
      const rects = range.getClientRects()
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i]
        if (r.height > 0 && r.width > 0) {
          rawRects.push(r)
        }
      }
    }

    if (rawRects.length === 0) return container.scrollHeight

    const visualLines = []
    rawRects.sort((a, b) => a.top - b.top)

    for (const rect of rawRects) {
      const line = visualLines.find(
        l => Math.abs(l.top - rect.top) < 6 || rect.top < l.bottom - 4,
      )

      if (line) {
        line.top = Math.min(line.top, rect.top)
        line.bottom = Math.max(line.bottom, rect.bottom)
        line.height = line.bottom - line.top
      } else {
        visualLines.push({
          top: rect.top,
          bottom: rect.bottom,
          height: rect.height,
        })
      }
    }

    if (visualLines.length < targetLineCount) {
      return container.scrollHeight
    }

    const targetLine = visualLines[targetLineCount - 1]

    return targetLine.top - containerRect.top + targetLine.height * 0.75
  }

  /**
   * @returns {number}
   */
  get lines () {
    return 4
  }

  /**
   * @returns {boolean}
   */
  get isExpanded () {
    return this.els.content.element.getAttribute('aria-expanded') === 'true'
  }

  /**
   * @returns {boolean}
   */
  get isHidden () {
    return this.els.inner.element.clientWidth === 0
  }

  /**
   * @returns {boolean}
   */
  get isDisabled () {
    if (this.startsCollapsed) return true

    return (this.dataset.readMoreDisabledSizes ?? '')
      .split(/\s+/)
      .includes(viewportSize())
  }
}

if (!customElements.get('read-more-description')) {
  customElements.define('read-more-description', ReadMoreDescription)
}
